import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  activityByDay,
  aggregateMastery,
  computeMastery,
  daysUntilDue,
  levelForXp,
  recentAccuracy,
  reviewForecast,
  topMisconceptions,
} from '@yuan-math/learning';
import { misconception } from '@yuan-math/exercises';
import { Card, MasteryBar, SectionHeading, Stat } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';

/**
 * The progress page.
 *
 * Everything here is a *learning* measurement (§30): mastery, accuracy,
 * retention, what is coming back for review. XP and levels are present but
 * deliberately secondary — they motivate, they do not measure.
 */
export function ProgressPage(): JSX.Element {
  const { profile } = useLearner();
  if (!profile) return <div />;

  const now = Date.now();
  const level = levelForXp(profile.xp);
  const accuracy = recentAccuracy(profile);
  const available = registry.availableConcepts();
  const overall = aggregateMastery(available.map((c) => c.id), profile.concepts, now);
  const forecast = reviewForecast(profile.concepts, now, 14);
  const activity = activityByDay(profile, now, 28);
  const busiestDay = Math.max(1, ...activity.map((day) => day.attempts));
  const mistakes = topMisconceptions(profile, 4);

  const studied = Object.values(profile.concepts)
    .filter((state) => state.attempts > 0 || state.lessonCompleted)
    .map((state) => ({
      state,
      concept: registry.concept(state.conceptId),
      mastery: computeMastery(state, now),
      due: daysUntilDue(state, now),
    }))
    .filter((row) => row.concept !== undefined)
    .sort((a, b) => b.mastery.overall - a.mastery.overall);

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">Progress</h1>
        <p className="page__lead">
          How much mathematics you have actually learned — measured by mastery and retention, not by how many boxes
          you ticked.
        </p>
      </header>

      <div className="dashboard-stats dashboard-stats--wide">
        <Stat label="Overall mastery" value={`${Math.round(overall * 100)}%`} hint={`of ${available.length} concepts`} />
        <Stat label="Concepts studied" value={studied.length} />
        <Stat label="Lessons finished" value={profile.stats.lessonsCompleted} />
        <Stat label="Questions answered" value={profile.stats.totalAttempts} />
        {accuracy !== undefined ? <Stat label="Recent accuracy" value={`${Math.round(accuracy * 100)}%`} /> : null}
        <Stat label="Longest streak" value={`${profile.streak.longest} days`} hint={`Level ${level.level} · ${profile.xp} XP`} />
      </div>

      <section className="dashboard-section">
        <SectionHeading title="Activity" description="Questions answered per day over the last four weeks." />
        <Card>
          <div className="activity-chart" role="img" aria-label={`Activity over the last ${activity.length} days`}>
            {activity.map((day) => (
              <div key={day.day} className="activity-chart__col" title={`${day.day}: ${day.attempts} questions`}>
                <div
                  className="activity-chart__bar"
                  style={{ height: `${Math.max(3, (day.attempts / busiestDay) * 100)}%` }}
                  data-empty={day.attempts === 0}
                />
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title="Coming back for review"
          description="Spaced repetition schedules each concept to return just before you would forget it."
        />
        <Card>
          <div className="forecast" role="img" aria-label="Review forecast for the next fortnight">
            {forecast.map((day) => (
              <div key={day.day} className="forecast__col">
                <div className="forecast__bar" style={{ height: `${Math.min(100, day.count * 24)}%` }} />
                <span className="forecast__label">{day.day === 0 ? 'today' : `+${day.day}`}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {mistakes.length > 0 ? (
        <section className="dashboard-section">
          <SectionHeading
            title="Patterns in your mistakes"
            description="Not a list of wrong answers — a list of ideas worth repairing."
          />
          <ul className="misconception-list">
            {mistakes.map((entry) => {
              const named = misconception(entry.id);
              if (!named) return null;
              const remediation = registry.concept(named.remediationConceptId);
              return (
                <li key={entry.id}>
                  <strong>{named.title}</strong> — seen {entry.count} {entry.count === 1 ? 'time' : 'times'}.
                  <p>{named.studentMessage}</p>
                  {remediation ? (
                    <Link to={`/concepts/${remediation.id}`} className="btn btn--ghost btn--sm">
                      Review {remediation.title}
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="dashboard-section">
        <SectionHeading title="Concept by concept" />
        {studied.length === 0 ? (
          <Card>
            <p>
              Nothing studied yet. <Link to="/courses">Pick a course</Link> or{' '}
              <Link to="/diagnostic">take the placement test</Link>.
            </p>
          </Card>
        ) : (
          <div className="table-wrap">
            <table className="progress-table">
              <thead>
                <tr>
                  <th scope="col">Concept</th>
                  <th scope="col">Mastery</th>
                  <th scope="col">Accuracy</th>
                  <th scope="col">Retention</th>
                  <th scope="col">Next review</th>
                </tr>
              </thead>
              <tbody>
                {studied.map((row) => (
                  <tr key={row.state.conceptId}>
                    <th scope="row">
                      <Link to={`/concepts/${row.state.conceptId}`}>{row.concept?.title}</Link>
                    </th>
                    <td>
                      <MasteryBar value={row.mastery.overall} size="sm" />
                    </td>
                    <td>{Math.round(row.mastery.accuracy * 100)}%</td>
                    <td>{Math.round(row.mastery.retention * 100)}%</td>
                    <td>{formatDue(row.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function formatDue(days: number | undefined): string {
  if (days === undefined) return '—';
  if (days <= 0) return 'Due now';
  if (days < 1) return 'Today';
  if (days < 2) return 'Tomorrow';
  return `In ${Math.round(days)} days`;
}
